/**
 * TableState — the serializable snapshot the table scene renders from.
 *
 * The rule this type exists to enforce: **the scene must work identically whether
 * this object came from the local engine or arrived over a WebSocket.** If the scene
 * can reach into engine internals it will, and then multiplayer is a rewrite instead
 * of a transport swap. So the scene gets this, and only this, plus an event stream.
 *
 * Two consequences worth stating because they are easy to erode:
 *
 *   - Plain JSON only. There is a test that `JSON.parse(JSON.stringify(state))` is
 *     deep-equal to `state`. Add a Map, a Set, a Date or an `undefined` and it fails.
 *   - It is built PER VIEWER. `hole` is populated only for seats that viewer is
 *     entitled to see. There is no "and the UI just won't render it" — a field that
 *     exists in the payload is a field a player can read.
 *
 * It also deliberately carries NO bot profile. Which archetype a seat is playing is
 * the answer key; see `identityFor`.
 */
import type { Card, Street, TableRules } from "./types";

export type CosmeticKind = "totem" | "taunt" | "avatar_frame" | "card_back";

/**
 * A cosmetic as the scene needs it: what to draw, and nothing else.
 *
 * PURELY VISUAL, and that is a legal constraint rather than a taste one. A cosmetic
 * may change how a seat LOOKS. It may never change a stack, a timer, a seat draw, a
 * blind level, an entry right, or what a player can see or do — because the prize
 * tournaments depend on free entry removing consideration, and an item that money
 * buys and that affects a tournament turns that structure into a lottery.
 *
 * The shape of this type is part of the guard: it carries an id and an asset key and
 * has nowhere to put a number that could mean an advantage.
 */
export interface CosmeticRef {
  id: string;
  kind: CosmeticKind;
  /** Key into the asset bundle, never a URL. */
  assetKey: string;
}

/**
 * What a seat has equipped, travelling inside the serialized seat state.
 *
 * Deliberately part of `SeatView` rather than fetched separately by the scene: it
 * then arrives over the same path a WebSocket will use, so multiplayer inherits
 * cosmetics with no extra work and no second code path to keep in step.
 *
 * `taunt` is the equipped choice, not something drawn on the seat — taunts fire as
 * events. It lives here so one object answers "what has this player selected".
 */
export interface Equipped {
  totem: CosmeticRef | null;
  avatarFrame: CosmeticRef | null;
  cardBack: CosmeticRef | null;
  taunt: CosmeticRef | null;
}

export const NO_COSMETICS: Equipped = {
  totem: null, avatarFrame: null, cardBack: null, taunt: null,
};

/** What a player sees about who they are sitting with. Never the archetype. */
export interface SeatIdentity {
  /** Display name, e.g. "Amos Creed". Randomised per session. */
  name: string;
  /** Short enough for a seat box on a phone. */
  shortName: string;
  /** Id into the curated portrait set, or null for the placeholder silhouette. */
  avatarId: string | null;
}

export interface SeatView {
  index: number;
  identity: SeatIdentity;
  stack: number;
  /** Put in during the current betting round. */
  committed: number;
  folded: boolean;
  allIn: boolean;
  sittingOut: boolean;
  /** True for the viewer's own seat. */
  isSelf: boolean;
  /** Purely visual items this seat has equipped. Never affects play. */
  equipped: Equipped;
  /**
   * Hole cards, or null when this viewer may not see them.
   *
   * Null is not "unknown to the engine" — the engine knows every card. Null means
   * "not yours to see", and it is applied here, at the boundary, rather than trusted
   * to the renderer.
   */
  hole: Card[] | null;
}

export interface TableState {
  /** Index of the hand in progress within the session. */
  handNumber: number;
  street: Street;
  board: Card[];
  pot: number;
  /** The amount a seat must have in this round to continue. */
  currentBet: number;
  /** Seat to act, or null when no one is. */
  toAct: number | null;
  button: number;
  seats: SeatView[];
  rules: TableRules;
  /** Hash of this hand's seed, published before the deal. */
  commitment: string;
}

/**
 * Assert a state is transport-safe.
 *
 * Called by tests rather than in production — the cost is a full serialize, and the
 * property it checks cannot regress at runtime without a code change that the test
 * will catch first.
 */
export function isTransportSafe(state: unknown): boolean {
  try {
    const round = JSON.parse(JSON.stringify(state));
    return deepEqual(round, state);
  } catch {
    return false;
  }
}

function isPlainContainer(v: object): boolean {
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === Array.prototype || proto === null;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (typeof a !== "object") return false;
  /**
   * Anything that is not a plain object or array fails here.
   *
   * Without this, a Set compares EQUAL to the `{}` that JSON turns it into — both
   * have zero enumerable own keys — so the one check standing between us and a
   * silently emptied collection on the wire would pass it through. Same for Map.
   * A Date is caught earlier, because JSON makes it a string and the typeof differs.
   */
  if (!isPlainContainer(a as object) || !isPlainContainer(b as object)) return false;
  const ka = Object.keys(a as object), kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => deepEqual(
    (a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}
